import type { FooterInfoData, Part } from '../types';
import type { ResolvedPageContext } from './resolvedPageContext';
import type { ResolvedRepairIdentity, SemanticPageData } from '../semanticPageTypes';

export const PUBLIC_SSR_CONTRACT = 'PUBLIC_PAGE_SSR_CONTRACT_V1';
export const BUSINESS_INFORMATION_AUTHORITY = 'BUSINESS_INFORMATION_AUTHORITY_V1';

interface PublicSsrBootstrap {
  contract: typeof PUBLIC_SSR_CONTRACT;
  repairIdentity?: ResolvedRepairIdentity;
  productCatalog?: Part[];
  resolvedSeo?: NonNullable<SemanticPageData['seo']> & {
    tenantId: number;
    siteId: number;
    locale: string;
  };
  businessInformation: Partial<FooterInfoData> & {
    business_information_authority: typeof BUSINESS_INFORMATION_AUTHORITY;
  };
}

export function readPublicSsrBootstrap(): PublicSsrBootstrap | null {
  if (typeof document === 'undefined') return null;
  const element = document.getElementById('mobilbo-public-ssr-bootstrap');
  if (!element?.textContent) return null;
  try {
    const parsed = JSON.parse(element.textContent) as Partial<PublicSsrBootstrap>;
    if (parsed.contract !== PUBLIC_SSR_CONTRACT) return null;
    if (parsed.businessInformation?.business_information_authority !== BUSINESS_INFORMATION_AUTHORITY) return null;
    return parsed as PublicSsrBootstrap;
  } catch {
    return null;
  }
}

// A read-only projection of the same scoped SEO result used to render the HTML.
// It must not follow the user onto another canonical route or site.
export function readServerSemanticPage(context: ResolvedPageContext | null | undefined): SemanticPageData | null {
  const seo = readPublicSsrBootstrap()?.resolvedSeo;
  if (!context || !seo || seo.canonical !== context.canonicalUrl
    || seo.siteId !== context.siteId || seo.tenantId !== context.tenantId
    || seo.locale !== context.locale || !seo.title || !seo.publicH1) return null;
  const repairIdentity = readPublicSsrBootstrap()?.repairIdentity;
  return { seo, ...(matchesRepairIdentity(repairIdentity, context) ? { repairIdentity } : {}) };
}

export function matchesRepairIdentity(identity: ResolvedRepairIdentity | undefined, context: ResolvedPageContext): identity is ResolvedRepairIdentity {
  return Boolean(identity && identity.canonical === context.canonicalUrl
    && identity.siteId === context.siteId && identity.tenantId === context.tenantId && identity.locale === context.locale
    && identity.modelId === String(context.modelId) && identity.brandId === String(context.brandId)
    && identity.model.id === identity.modelId && identity.brand.id === identity.brandId
    && identity.relationshipId === (context.modelServiceRelationshipId == null ? null : String(context.modelServiceRelationshipId))
    && Array.isArray(identity.model.repairs));
}
